/**
 * ExceptionPolicyPage — 异常策略管理
 *
 * CRUD interface for exception policies that override default domain rules
 * for specific tenants. Policies are stored in the global `policies` table
 * with key pattern `exception:{tenantId}:{policyType}:{ruleKey}`.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { ShieldAlert, Plus, Trash2, RefreshCw, Loader2, X } from 'lucide-react';
import AdminTenantScopeBar from '@/components/AdminTenantScopeBar';
import { api } from '@/lib/api';
import { toast } from 'sonner';

interface ExceptionPolicy {
  id: string;
  key: string;
  tenantId: string;
  policyType: string;
  ruleKey: string;
  value: unknown;
  reason: string;
  expiresAt: string | null;
  createdBy?: string;
  created_at: string;
  updated_at: string;
}

const POLICY_TYPES = [
  'ring_override',
  'channel_override',
  'feature_flag',
  'rate_limit',
  'module_access',
  'custom',
] as const;

export default function ExceptionPolicyPage() {
  const [policies, setPolicies] = useState<ExceptionPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [scopeTenantId, setScopeTenantId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form, setForm] = useState({
    tenantId: '',
    policyType: 'feature_flag' as string,
    ruleKey: '',
    value: '',
    reason: '',
    expiresAt: '',
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get<ExceptionPolicy[]>('/platform/exception-policies');
      setPolicies(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Filter by scoped tenant
  const filtered = scopeTenantId
    ? policies.filter(p => p.tenantId === scopeTenantId)
    : policies;

  // Create policy
  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.tenantId || !form.ruleKey || !form.reason) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      let parsedValue: unknown = form.value;
      try { parsedValue = JSON.parse(form.value); } catch { /* keep as string */ }

      await api.post('/platform/exception-policies', {
        tenantId: form.tenantId,
        policyType: form.policyType,
        ruleKey: form.ruleKey,
        value: parsedValue,
        reason: form.reason,
        expiresAt: form.expiresAt || undefined,
      });
      toast.success('Exception policy created');
      setShowForm(false);
      setForm({ tenantId: '', policyType: 'feature_flag', ruleKey: '', value: '', reason: '', expiresAt: '' });
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to create policy');
    }
    setSubmitting(false);
  };

  // Delete policy
  const handleDelete = async (key: string) => {
    if (!confirm(`Delete policy "${key}"?`)) return;
    try {
      await api.delete(`/platform/exception-policies/${encodeURIComponent(key)}`);
      toast.success('Policy deleted');
      await load();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete');
    }
  };

  // When scopeTenantId changes, pre-fill form tenantId
  useEffect(() => {
    if (scopeTenantId) {
      setForm(f => ({ ...f, tenantId: scopeTenantId }));
    }
  }, [scopeTenantId]);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ShieldAlert className="w-6 h-6 text-amber-600" />
          <div>
            <h1 className="text-xl font-bold">Exception Policies</h1>
            <p className="text-sm text-gray-500">覆盖特定租户的默认策略（ring/channel/feature/rate）</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            {showForm ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
            {showForm ? 'Cancel' : 'New Policy'}
          </button>
        </div>
      </div>

      {/* Scope */}
      <AdminTenantScopeBar value={scopeTenantId} onChange={setScopeTenantId} showRingBadge />

      {/* Create Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-800 space-y-3">
          <h3 className="text-sm font-semibold">Create Exception Policy</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Tenant ID *</label>
              <input
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="00000000-0000-..."
                value={form.tenantId}
                onChange={e => setForm(f => ({ ...f, tenantId: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Policy Type *</label>
              <select
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={form.policyType}
                onChange={e => setForm(f => ({ ...f, policyType: e.target.value }))}
              >
                {POLICY_TYPES.map(pt => (
                  <option key={pt} value={pt}>{pt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Rule Key *</label>
              <input
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="e.g. max_jobs_per_day"
                value={form.ruleKey}
                onChange={e => setForm(f => ({ ...f, ruleKey: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Value (JSON or string)</label>
              <input
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder='e.g. true, 100, "custom"'
                value={form.value}
                onChange={e => setForm(f => ({ ...f, value: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Reason *</label>
              <input
                className="w-full border rounded px-2 py-1.5 text-sm"
                placeholder="Why this exception?"
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                required
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Expires At (optional)</label>
              <input
                type="datetime-local"
                className="w-full border rounded px-2 py-1.5 text-sm"
                value={form.expiresAt}
                onChange={e => setForm(f => ({ ...f, expiresAt: e.target.value }))}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-1.5 px-4 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Create Policy
          </button>
        </form>
      )}

      {/* Loading / Error */}
      {loading && (
        <div className="flex items-center gap-2 py-8 justify-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading policies…
        </div>
      )}
      {error && <div className="text-red-600 text-sm py-4">{error}</div>}

      {/* Policies Table */}
      {!loading && (
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-800 border-b">
                <th className="px-3 py-2 text-left font-medium text-gray-600">Key</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Value</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Reason</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Expires</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Updated</th>
                <th className="px-3 py-2 text-center font-medium text-gray-600 w-[60px]" />
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id} className="border-b hover:bg-gray-50/50 dark:hover:bg-gray-800/50">
                  <td className="px-3 py-2 font-mono text-xs">{p.key}</td>
                  <td className="px-3 py-2">
                    <span className="px-1.5 py-0.5 rounded text-xs bg-indigo-100 text-indigo-700">
                      {p.policyType}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs max-w-[200px] truncate">
                    {typeof p.value === 'object' ? JSON.stringify(p.value) : String(p.value ?? '—')}
                  </td>
                  <td className="px-3 py-2 text-gray-600 max-w-[200px] truncate">{p.reason}</td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {p.expiresAt ? new Date(p.expiresAt).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-400">
                    {new Date(p.updated_at).toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-center">
                    <button
                      onClick={() => handleDelete(p.key)}
                      className="text-red-500 hover:text-red-700 p-1"
                      title="Delete policy"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="text-center py-8 text-gray-400 text-sm">
              {policies.length === 0 ? 'No exception policies yet' : 'No policies match the selected tenant'}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      {!loading && policies.length > 0 && (
        <div className="text-xs text-gray-400">
          Total: {policies.length} policies · Showing: {filtered.length}
        </div>
      )}
    </div>
  );
}
